import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent, Grid, TextField, MenuItem, DialogActions,
    useTheme, useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const GeneralLedgerPage = () => {
    const [entries, setEntries] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        debitAccountId: '',
        creditAccountId: '',
        amount: '',
        description: ''
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchEntries = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('accounts/ledger'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEntries(res.data);
        } catch (error) {
            console.error('Fetch ledger error:', error);
        }
    };

    const fetchAccounts = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('accounts'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAccounts(res.data);
        } catch (error) {
            console.error('Fetch accounts error:', error);
        }
    };

    useEffect(() => {
        fetchEntries();
        fetchAccounts();
    }, []);

    const handleCreateJournal = async () => {
        try {
            if (!formData.debitAccountId || !formData.creditAccountId || !formData.amount || !formData.description) {
                alert('Please fill in all fields');
                return;
            }

            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.post(getApiUrl('accounts/journal'), formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOpenDialog(false);
            setFormData({ debitAccountId: '', creditAccountId: '', amount: '', description: '' });
            fetchEntries();
        } catch (error: any) {
            console.error('Create journal error:', error);
            alert(`Failed: ${error.response?.data?.message || error.message}`);
        }
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={4} spacing={2}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C">General Ledger</Typography>
                    <Typography variant="body2" color="text.secondary">View all financial transactions and postings</Typography>
                </Box>
                <Button
                    variant="contained"
                    fullWidth={isSmallMobile}
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ bgcolor: '#263B7C', borderRadius: '8px', px: 3 }}
                >
                    NEW JOURNAL ENTRY
                </Button>
            </Stack>

            <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: isMobile ? 800 : 'auto' }}>
                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>DATE</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>ACCOUNT</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>DESCRIPTION</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>REF</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">DEBIT</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">CREDIT</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.map((entry) => (
                                <TableRow key={entry.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {new Date(entry.postedAt).toLocaleDateString()}<br />
                                        <Typography variant="caption" color="text.secondary">{new Date(entry.postedAt).toLocaleTimeString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{entry.account.code}</Typography>
                                        <Typography variant="body2" fontWeight={600}>{entry.account.name}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>{entry.description}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {entry.invoice?.invoiceNo && <Chip label={entry.invoice.invoiceNo} size="small" variant="outlined" color="primary" />}
                                            {entry.receipt?.receiptNo && <Chip label={entry.receipt.receiptNo} size="small" variant="outlined" color="success" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: entry.debit > 0 ? '#E53E3E' : 'inherit', fontWeight: entry.debit > 0 ? 600 : 400 }}>
                                        {Number(entry.debit) > 0 ? `$${Number(entry.debit).toLocaleString()}` : '-'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: entry.credit > 0 ? '#38A169' : 'inherit', fontWeight: entry.credit > 0 ? 600 : 400 }}>
                                        {Number(entry.credit) > 0 ? `$${Number(entry.credit).toLocaleString()}` : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isSmallMobile}>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white' }}>New Manual Journal Entry</DialogTitle>
                <DialogContent sx={{ p: isSmallMobile ? 2 : 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                        Use this form to make manual adjustments or corrections. This will create a permanent audit record.
                    </Typography>
                    <Grid container spacing={isSmallMobile ? 2 : 3}>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Description / Reason"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="e.g. Correction for Inv-001"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth select label="Debit Account"
                                value={formData.debitAccountId}
                                onChange={(e) => setFormData({ ...formData, debitAccountId: e.target.value })}
                                helperText="Account receiving value"
                            >
                                {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.code} - {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth select label="Credit Account"
                                value={formData.creditAccountId}
                                onChange={(e) => setFormData({ ...formData, creditAccountId: e.target.value })}
                                helperText="Account giving value"
                            >
                                {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.code} - {a.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Amount" type="number"
                                value={formData.amount}
                                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: isSmallMobile ? 2 : 3 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateJournal} sx={{ bgcolor: '#F9B43F' }}>Post Entry</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GeneralLedgerPage;
